import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import starImage from "../../assets/landing/star.png";

import { useLanguage } from "../../contexts/LanguageContext";
import EqualizerMotion from "../../components/Landing/EqualizerMotion";
import PromoModal from "../Popups/PromoModal";
import LandingHeader from "../Header/LandingHeader";
import { fetchAdminCoupon } from "../../services/landingThunks";
import { useThunk } from "../../hooks/useThunk";
import { useWeakBrowser } from "../../hooks/useWeakBrowser";

const container = {
  hidden: { opacity: 0, y: -110 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 0 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7 },
  },
};

export default function Content() {
  const navigate = useNavigate();
  const { translations, language } = useLanguage();
  const [promoOpen, setPromoOpen] = useState(false);
  const [couponData, setCouponData] = useState(null);
  const [stars, setStars] = useState([]);

  const [doFetchAdminCoupon] = useThunk(fetchAdminCoupon);
  const isWeakBrowser = useWeakBrowser();

  useEffect(() => {
    (async () => {
      const res = await doFetchAdminCoupon();
      // Extract coupon data from response
      let data = null;
      if (res?.response?.data) {
        data = res.response.data;
      }
      setCouponData(data);
    })();
  }, [doFetchAdminCoupon]);

  useEffect(() => {
    // Generate stars using React state (lightweight for Safari/weak browsers)
    const starCount = isWeakBrowser ? 25 : 35;
    const newStars = Array.from({ length: starCount }).map((_, i) => ({
      id: i,
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
      size: 10 + Math.random() * 10,
      delay: `${Math.random() * 3}s`,
      duration: `${2 + Math.random() * 3}s`,
    }));
    setStars(newStars);
  }, [isWeakBrowser]);

  const getCouponDescription = () => {
    if (!couponData?.description) return "";

    // If language is "en", try English first, then German
    if (language === "en") {
      return couponData.description.en || couponData.description.de || "";
    }

    // If language is "de", try German first, then English
    if (language === "de") {
      return couponData.description.de || couponData.description.en || "";
    }

    // Default fallback
    return couponData.description.en || couponData.description.de || "";
  };

  return (
    <section
      id="landing-hero"
      className="relative overflow-visible z-30 min-h-[400px] lg:min-h-[800px] xl:min-h-[868px] text-[#DBFFFB] scroll-mt-24"
    >
      {/* Star Background rendered with React */}
      <div className="absolute inset-0 w-full h-full z-0 pointer-events-none overflow-hidden">
        {stars.map((star) => (
          <img
            key={star.id}
            src={starImage}
            alt="star"
            className="animated-star absolute pointer-events-none"
            style={{
              top: star.top,
              left: star.left,
              width: `${star.size}px`,
              height: `${star.size}px`,
              animationDelay: star.delay,
              animationDuration: star.duration,
              willChange: "transform, opacity",
              transform: "translate3d(0,0,0)",
              WebkitBackfaceVisibility: "hidden",
              backfaceVisibility: "hidden",
            }}
          />
        ))}
      </div>

      {/* Desktop & Tablet Blur */}
      <div className="hidden sm:block blur-glow" />

      {/* Mobile Blur */}
      <div className="block sm:hidden blur-glow-mobile" />

      {/* Header */}
      <div className="mb-24 xs:mb-12 sm:mb-0">
        <LandingHeader
          translations={translations}
          onPromoOpen={() => setPromoOpen(true)}
        />
      </div>

      {/* Main Content */}
      <motion.div
        className="relative z-10 flex flex-col items-center justify-center text-center px-4 pb-12 xs:pb-28 sm:pb-36 md:pb-36 lg:pb-20 pt-[60px] sm:pt-[80px] md:pt-[100px] lg:pt-[115px]"
        variants={container}
        initial="hidden"
        animate="show"
        transformTemplate={({ y, opacity }) => `translate3d(0, ${y}, 0)`}
        style={{
          willChange: "transform, opacity",
          WebkitBackfaceVisibility: "hidden",
          backfaceVisibility: "hidden",
        }}
      >
        <motion.div
          className="flex items-end justify-center flex-wrap bg-[linear-gradient(180deg,#52FAF0_31%,#197AD0_91.1%)] bg-clip-text text-transparent font-public-sans font-[600] text-[28px] xs:text-[32px] sm:text-[40px] md:text-[64px] lg:text-[64px] xs:leading-[75px] tracking-[0%] space-x-4"
          variants={item}
        >
          <span>{translations?.landingpage?.headline1 || "The Record"}</span>

          <div className="relative -translate-y-3 xs:-translate-y-6 md:-translate-y-[18px]">
            <EqualizerMotion />
          </div>

          <span>{translations?.landingpage?.headline2 || "Pool"}</span>
        </motion.div>

        <motion.div
          className="mt-4 font-public-sans uppercase font-extrabold text-white text-[27px] xs:text-[32px] md:text-[48px] lg:text-[60px] tracking-[0.07em] leading-10 sm:leading-[75px] max-w-full px-4"
          variants={item}
        >
          {translations?.landingpage?.subheadline || "Powering DJs Worldwide"}
        </motion.div>

        <motion.div
          className="mt-4 tracking-[0%] h-auto lg:h-[75px] font-public-sans font-normal text-white text-[10px] xs:text-[12px] md:text-[14px] lg:text-[16px] leading-[19px] max-w-[90%] md:max-w-[738px] mx-auto"
          variants={item}
        >
          {translations?.landingpage?.description ||
            "The #1 source for DJ-exclusive tracks, powerful tools, and fresh edits-made to move crowds and keep you one step ahead. Surprise your audience with sounds they won't hear anywhere else."}
        </motion.div>

        <div className="relative mt-[42px] w-[360px] group flex justify-center">
          <button
            onClick={() => navigate("/signup")}
            className="bg-[#000101] font-public-sans text-white font-medium rounded-[100px] relative z-10 px-2 sm:px-4 py-3 sm:py-5 transition-all duration-300 w-[190px] sm:w-[220px] h-auto md:w-[273px] md:h-auto lg:h-auto border-2 border-transparent bg-gradient-to-b from-[#52FAF0] via-[#36BAE0] to-[#197AD0] bg-origin-border"
            style={{
              backgroundImage:
                "linear-gradient(#000101, #000101), linear-gradient(180deg, #52FAF0 1.84%, #36BAE0 21.65%, #197AD0 106.4%)",
              backgroundClip: "padding-box, border-box",
              backgroundOrigin: "border-box",
              boxShadow: "0px 11px 117.9px 0px #0000008C",
            }}
          >
            <span className="text-[12px] xs:text-[18px] md:text-[26px] lg:text-[30px] uppercase font-medium font-public-sans leading-[100%]">
              {translations?.landingpage?.cta1 || "Join for Free"}
            </span>
          </button>

          <div className="absolute z-0 top-[70%] rounded-[16px] blur-[20px] bg-[#4EEEE4] opacity-[80%] w-[270px] h-[28px] md:w-[330px] md:h-[20px] lg:w-[360px] lg:h-[41px] group-hover:w-[200px] group-hover:h-[20px] transform-gpu group-hover:translate-y-[20%]" />
        </div>
      </motion.div>
      <PromoModal
        open={promoOpen}
        onClose={() => setPromoOpen(false)}
        translations={translations}
        language={language}
        couponCode={couponData?.code}
        couponDescription={getCouponDescription()}
      />
    </section>
  );
}

/**
 * OPTIONAL: Call this from anywhere (e.g., a navbar button)
 * to smooth-scroll down to this section.
 */
export function scrollToLandingHero() {
  const el = document.getElementById("landing-hero");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}
